import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export interface AppConfig {
  supabaseUrl: string;
  supabaseKey: string;
  devLogin: boolean;
  aiEnabled: boolean;
  aiModel: string;
}

export interface CurrentUser {
  id: string;
  email: string | null;
  name: string | null;
  avatarUrl: string | null;
}

interface AuthState {
  config: AppConfig | null;
  user: CurrentUser | null;
  loading: boolean;
  signInGoogle: () => Promise<void>;
  signInDev: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

let supabase: SupabaseClient | null = null;
const DEV_KEY = "tb:dev-token";

const storage = {
  get(k: string) {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set(k: string, v: string | null) {
    try {
      if (v == null) localStorage.removeItem(k);
      else localStorage.setItem(k, v);
    } catch {
      /* приватный режим */
    }
  },
};

/** Токен для запросов к API (Supabase JWT или dev-токен) */
export async function getAccessToken(): Promise<string | null> {
  const dev = storage.get(DEV_KEY);
  if (dev) return dev;
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

function userFromSession(s: Session | null): CurrentUser | null {
  if (!s?.user) return null;
  const m = (s.user.user_metadata ?? {}) as Record<string, string | undefined>;
  return { id: s.user.id, email: s.user.email ?? null, name: m.full_name ?? m.name ?? s.user.email ?? null, avatarUrl: m.avatar_url ?? m.picture ?? null };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    (async () => {
      let cfg: AppConfig | null = null;
      try {
        const r = await fetch("/api/config");
        cfg = (await r.json()) as AppConfig;
        try {
          localStorage.setItem("tb:config", JSON.stringify(cfg));
        } catch {
          /* ignore */
        }
      } catch {
        // офлайн — берём последнюю конфигурацию
        const cached = storage.get("tb:config");
        cfg = cached ? (JSON.parse(cached) as AppConfig) : null;
      }
      setConfig(cfg);
      const dev = storage.get(DEV_KEY);
      if (dev && cfg?.devLogin) {
        const email = dev.slice(4);
        setUser({ id: "dev", email, name: email.split("@")[0], avatarUrl: null });
        // настоящий id пользователя берём у сервера
        fetch("/api/me", { headers: { Authorization: `Bearer ${dev}` } })
          .then((r) => (r.ok ? r.json() : null))
          .then((u: CurrentUser | null) => u && setUser(u))
          .catch(() => undefined);
        setLoading(false);
        return;
      }
      if (dev && !cfg?.devLogin) storage.set(DEV_KEY, null);
      if (cfg?.supabaseUrl && cfg.supabaseKey) {
        supabase ??= createClient(cfg.supabaseUrl, cfg.supabaseKey, {
          auth: { flowType: "pkce", persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, storageKey: "tb-auth" },
        });
        const { data } = await supabase.auth.getSession();
        setUser(userFromSession(data.session));
        const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setUser(userFromSession(s)));
        unsub = () => sub.subscription.unsubscribe();
        // убрать ?code= из адресной строки после входа
        if (location.search.includes("code=")) history.replaceState(null, "", location.pathname);
      }
      setLoading(false);
    })();
    return () => unsub?.();
  }, []);

  const signInGoogle = useCallback(async () => {
    if (!supabase) return;
    const next = location.pathname + location.search;
    storage.set("tb:after-login", next);
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${location.origin}/auth/callback`, queryParams: { prompt: "select_account" } },
    });
  }, []);

  const signInDev = useCallback(async (email: string) => {
    const token = `dev:${email.trim().toLowerCase() || "demo@local"}`;
    storage.set(DEV_KEY, token);
    const r = await fetch("/api/me", { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setUser((await r.json()) as CurrentUser);
  }, []);

  const signOut = useCallback(async () => {
    storage.set(DEV_KEY, null);
    try {
      for (const k of Object.keys(localStorage)) if (/^tb:(bundle|thread|last-trip)/.test(k)) localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
    try {
      if ("caches" in window) await caches.delete("trip-data");
    } catch {
      /* ignore */
    }
    if (supabase) await supabase.auth.signOut();
    setUser(null);
    location.href = "/";
  }, []);

  const value = useMemo(() => ({ config, user, loading, signInGoogle, signInDev, signOut }), [config, user, loading, signInGoogle, signInDev, signOut]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth вне AuthProvider");
  return v;
}

export function takeAfterLogin() {
  const v = storage.get("tb:after-login");
  storage.set("tb:after-login", null);
  return v;
}
